import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchStyleExamples } from 'api/video';
import { StyleExamplesState } from 'components/video-gen/styleExamples';

/**
 * SYM-6226. The three style examples for a draft, asked for once.
 *
 * ASKED ONCE PER DRAFT, AND HELD. The examples are built from things the
 * customer cannot change from inside the wizard -- the draft's own pictures,
 * the brand's colour and logo, and two fixed English words -- so the answer
 * cannot go out of date while the wizard is open. Asking again on every visit
 * to the Style step would re-run a person detector over the draft's
 * photographs for a picture that cannot have moved.
 *
 * ASKED AS SOON AS THERE IS A DRAFT, not when the Style step is opened. Since
 * SYM-6308 the wizard opens on Preview, so the Style step is at least one
 * press away and the call has that press to finish in. The cost is one request
 * for a customer who never opens Style; the alternative is that everyone who
 * does open it waits at the moment they are looking.
 *
 * ⚠️ NOT A CACHE ACROSS DRAFTS. A new draft is a new id and a new answer; this
 * keeps exactly one.
 *
 * ⚠️ ONE THING THE CUSTOMER *CAN* CHANGE FROM OUTSIDE, and we are the ones who
 * send them to do it. When their brand has no colour, the step says so and
 * links to the brand settings, in a NEW TAB so the wizard is not thrown away.
 * They set a colour, come back -- and without this the examples are still navy
 * and the message still tells them to go and set the colour they just set. A
 * customer who follows our own instruction and returns to the same wrong screen
 * concludes it did not work.
 *
 * So the answer is asked for again when the tab is looked at again, and ONLY
 * while the brand has no colour of its own. That is the only state anything
 * outside the wizard can change, so every other draft pays nothing for this: no
 * request on an ordinary tab switch, and no second run of the person detector.
 * Found by the review gate, 22 Sep 2026, on the new tab that created the path.
 */
export const useStyleExamples = (
  draftId: number | null | undefined,
  /**
   * Whether the wizard is on screen.
   *
   * ⚠️ This provider outlives the wizard: closing it leaves the draft's id
   * behind, so without this the re-ask below would keep firing on every tab
   * switch for a wizard nobody has open, forever. Found by the review gate,
   * 22 Sep 2026. It does NOT gate the first fetch, which is deliberately made
   * before the Style step is reached.
   */
  wizardIsOpen = true
): { state: StyleExamplesState; retry: () => void } => {
  const [state, setState] = useState<StyleExamplesState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  /**
   * Which answer is still wanted.
   *
   * Compared rather than cancelled: a reply for a draft that has since been
   * replaced, or for an attempt that has since been retried, is dropped where
   * it arrives. Doing it this way rather than with a cleanup flag means the
   * effect running twice -- which React does in development on purpose --
   * still ends with an answer on screen instead of a permanent "loading".
   */
  const wanted = useRef('');
  /** The draft the answer on screen belongs to. */
  const shownFor = useRef<number | null>(null);
  /**
   * The request still on its way, and whether the customer came back to the
   * tab again while it was. ONE request at a time: two overlapping re-asks let
   * an older, failed one decide what is shown after a newer one had already
   * brought the colour (review gate, 23 Sep 2026). A return during a request
   * is not dropped either -- the colour may have been saved after that request
   * left -- so it asks once more when the request is done.
   */
  const inFlight = useRef<string | null>(null);
  const askAgainAfter = useRef(false);

  useEffect(() => {
    if (!draftId) {
      // No draft, nothing to show. Not "failed": nothing has been refused.
      wanted.current = '';
      setState({ status: 'loading' });
      return;
    }

    const key = `${draftId}:${attempt}`;
    wanted.current = key;
    inFlight.current = key;
    if (shownFor.current !== draftId) askAgainAfter.current = false;
    const settle = () => {
      if (inFlight.current !== key) return;
      inFlight.current = null;
      if (askAgainAfter.current) {
        askAgainAfter.current = false;
        setAttempt((n) => n + 1);
      }
    };
    // A re-ask for the SAME draft keeps what is on screen until the new answer
    // is here (Steven, 23 Sep 2026: coming back from the brand settings, the
    // examples went grey for more than ten seconds while the server drew them
    // again). Only a new draft starts from the grey blocks: its old answer is
    // for other pictures.
    setState((shown) =>
      shownFor.current === draftId && shown.status === 'ready'
        ? shown
        : { status: 'loading' }
    );

    fetchStyleExamples(draftId)
      .then((answer) => {
        if (wanted.current !== key) return;
        shownFor.current = draftId;
        setState({
          status: 'ready',
          examples: answer?.examples ?? [],
          brandHasOwnColour: !!answer?.brand_has_own_colour,
          brandId: answer?.brand_id ?? null,
        });
      })
      .catch(() => {
        if (wanted.current !== key) return;
        // A failed re-ask leaves the answer that is already on screen: it is
        // for this draft, and a working step beats a failure notice.
        setState((shown) =>
          shownFor.current === draftId && shown.status === 'ready'
            ? shown
            : { status: 'failed' }
        );
      })
      .finally(settle);
  }, [draftId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // See the note at the top: the brand settings are a tab away, and this is the
  // one answer a customer can make wrong by coming back.
  const brandColourCouldHaveBeenSet =
    wizardIsOpen && state.status === 'ready' && !state.brandHasOwnColour;

  useEffect(() => {
    if (!draftId || !brandColourCouldHaveBeenSet) return undefined;

    const askAgain = () => {
      // `visibilitychange` fires on hiding as well as showing, and a wizard
      // nobody is looking at has nothing to redraw.
      if (document.visibilityState !== 'visible') return;
      if (inFlight.current) askAgainAfter.current = true;
      else setAttempt((n) => n + 1);
    };

    document.addEventListener('visibilitychange', askAgain);

    return () => document.removeEventListener('visibilitychange', askAgain);
  }, [draftId, brandColourCouldHaveBeenSet]);

  return { state, retry };
};

export default useStyleExamples;
